<script lang="ts" setup>
import { computed, toRef } from 'vue';
import { getDefaultLocaleValue } from '../locales/default';
import { useTranslations } from '@/composables/useTranslations';
import {
  EngineTypeLabel,
  TechDataVehicle,
  Translations,
  TransmissionMode,
} from '@/types/types';

const props = defineProps<{
  vehicles: TechDataVehicle[];
  locale: string;
  parentLabel: string;
  label: string;
  translations: Translations;
  transmissionMode: TransmissionMode | null;
}>();

const { getEngineTypeTranslation } = useTranslations(
  toRef(props, 'translations'),
);

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

type DataPoint =
  | string
  | {
      unit?: string;
      individual?: unknown;
      value?: unknown;
      min?: unknown;
      max?: unknown;
    }
  | null
  | undefined;

function getDataPointStringForLocale(dp: DataPoint, locale: string): string {
  if (dp == null) return '-';
  if (typeof dp === 'string') return dp;

  const fmt = new Intl.NumberFormat(locale);
  const unitSuffix = dp.unit ? ` ${dp.unit}` : '';

  const format = (n: any) => {
    if (typeof n === 'number') return fmt.format(n);
    if (typeof n === 'string') return n;
    return String(n);
  };

  // individual
  if (dp.individual != null) {
    return `${format(dp.individual)}${unitSuffix}`.trim();
  }

  // value
  if (dp.value != null) {
    return `${format(dp.value)}${unitSuffix}`.trim();
  }

  // range
  if (dp.min != null && dp.max != null) {
    const min = format(dp.min);
    const max = format(dp.max);

    if (dp.min === dp.max) return `${max}${unitSuffix}`.trim();
    return `${min} - ${max}${unitSuffix}`.trim();
  }

  return '';
}

function resolveDataPoint(
  parent: Record<string, any> | undefined,
  label: string,
  parentLabel: string,
) {
  const m = mode.value;

  if (!parent || !m) return undefined;

  return ['weights', 'emissionsConsumption'].includes(parentLabel)
    ? parent?.[m]?.[label]
    : parent[label];
}

const values = computed(() => {
  return (props.vehicles ?? []).map((v) => {
    const parent = (v as Record<string, any>)[props.parentLabel];
    const dp = resolveDataPoint(parent, props.label, props.parentLabel);
    return getDataPointStringForLocale(dp, props.locale);
  });
});

function isEngineTypeLabel(val: string): val is EngineTypeLabel {
  return ['DIESEL', 'PETROL', 'ELECTRIC'].includes(val);
}
</script>

<template>
  <tr>
    <th class="owvans-tech-data-row__label" scope="row">
      <span>{{
        translations[`owvc_tech_data_${parentLabel}_${label}`] ||
        getDefaultLocaleValue(`${parentLabel}_${label}`)
      }}</span>
    </th>
    <td v-for="(val, idx) in values" :key="idx">
      <span>
        {{ isEngineTypeLabel(val) ? getEngineTypeTranslation(val) : val }}
      </span>
    </td>
  </tr>
</template>

<style lang="scss" scoped>
tr {
  border-bottom: var(--wb-border-weight-main) solid
    var(--wb-color-border-main-default);

  text-align: right;
}

tr:last-child {
  border-bottom: none;
}

.v-has-scroll-x .owvans-tech-data-row__label::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;

  transform: translateX(100%);

  width: 4px;

  pointer-events: none;
  background:
    linear-gradient(to right, rgb(0 0 0 / 0.16) 0, transparent 65%),
    linear-gradient(to left, rgb(255 255 255 / 0.28) 0, transparent 55%);
}

.owvans-tech-data-row__label {
  position: sticky;
  left: 0;
  z-index: 2;

  width: min(40vw, 210px);

  text-align: left;
  overflow-wrap: break-word;

  background-color: var(--wb-color-shape-on-brand-default);
}
</style>
